<template>
  <div>
    <b-tabs content-class="mt-3">
      <!-- Audit Logs -->
      <b-tab title="Audit Logs" active>
        <b-row class="mb-3">
          <b-col md="3">
            <b-form-select v-model="auditFilters.direction" :options="directionOptions" @change="fetchAuditLogs(1)">
              <template #first>
                <b-form-select-option :value="''">All Directions</b-form-select-option>
              </template>
            </b-form-select>
          </b-col>
          <b-col md="3">
            <b-form-select v-model="auditFilters.status" :options="statusOptions" @change="fetchAuditLogs(1)">
              <template #first>
                <b-form-select-option :value="''">All Statuses</b-form-select-option>
              </template>
            </b-form-select>
          </b-col>
          <b-col md="3">
            <b-form-input v-model="auditFilters.from" type="date" placeholder="From" @change="fetchAuditLogs(1)" />
          </b-col>
          <b-col md="3">
            <b-form-input v-model="auditFilters.to" type="date" placeholder="To" @change="fetchAuditLogs(1)" />
          </b-col>
        </b-row>

        <b-table
          :items="auditLogs"
          :fields="auditFields"
          :busy="loadingAudit"
          responsive
          small
          show-empty
        >
          <template #cell(direction)="{ item }">
            <b-badge :variant="item.direction === 'inbound' ? 'info' : 'warning'">{{ item.direction }}</b-badge>
          </template>
          <template #cell(status)="{ item }">
            <b-badge :variant="item.status === 'success' ? 'success' : 'danger'">{{ item.status }}</b-badge>
          </template>
          <template #cell(created_at)="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>
          <template #table-busy>
            <div class="text-center my-3"><b-spinner variant="primary" /></div>
          </template>
        </b-table>

        <b-pagination
          v-if="auditLogsPagination"
          v-model="auditPage"
          :total-rows="auditLogsPagination.total"
          :per-page="auditLogsPagination.pageSize"
          align="center"
          @input="fetchAuditLogs"
        />
      </b-tab>

      <!-- Webhook Delivery Logs -->
      <b-tab title="Webhook Logs">
        <b-row class="mb-3">
          <b-col md="4">
            <b-form-select v-model="webhookLogFilters.status" :options="webhookStatusOptions" @change="fetchWebhookLogs(1)">
              <template #first>
                <b-form-select-option :value="''">All Statuses</b-form-select-option>
              </template>
            </b-form-select>
          </b-col>
        </b-row>

        <b-table
          :items="webhookLogs"
          :fields="webhookLogFields"
          :busy="loadingWebhookLogs"
          responsive
          small
          show-empty
        >
          <template #cell(status)="{ item }">
            <b-badge :variant="item.status === 'delivered' ? 'success' : (item.status === 'failed' ? 'danger' : 'warning')">
              {{ item.status }}
            </b-badge>
          </template>
          <template #cell(response_code)="{ item }">
            <code>{{ item.response_code }}</code>
          </template>
          <template #cell(created_at)="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>
          <template #table-busy>
            <div class="text-center my-3"><b-spinner variant="primary" /></div>
          </template>
        </b-table>

        <b-pagination
          v-if="webhookLogsPagination"
          v-model="webhookLogPage"
          :total-rows="webhookLogsPagination.total"
          :per-page="webhookLogsPagination.pageSize"
          align="center"
          @input="fetchWebhookLogs"
        />
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
export default {
  props: {
    connectorId: { type: Number, required: true },
  },
  data() {
    return {
      loadingAudit: false,
      loadingWebhookLogs: false,
      auditPage: 1,
      webhookLogPage: 1,
      auditFilters: { direction: '', status: '', from: '', to: '' },
      webhookLogFilters: { status: '' },
      auditFields: [
        { key: 'direction', label: 'Direction' },
        { key: 'action', label: 'Action' },
        { key: 'entity_type', label: 'Entity' },
        { key: 'status', label: 'Status' },
        { key: 'created_at', label: 'Time' },
      ],
      webhookLogFields: [
        { key: 'event_type', label: 'Event' },
        { key: 'target_endpoint', label: 'Endpoint' },
        { key: 'status', label: 'Status' },
        { key: 'response_code', label: 'Code' },
        { key: 'attempt', label: 'Attempt' },
        { key: 'created_at', label: 'Time' },
      ],
      directionOptions: [
        { value: 'inbound', text: 'Inbound' },
        { value: 'outbound', text: 'Outbound' },
      ],
      statusOptions: [
        { value: 'success', text: 'Success' },
        { value: 'error', text: 'Error' },
      ],
      webhookStatusOptions: [
        { value: 'delivered', text: 'Delivered' },
        { value: 'failed', text: 'Failed' },
        { value: 'pending', text: 'Pending' },
      ],
    }
  },
  computed: {
    auditLogs() {
      return this.$store.state.appConnector.auditLogs
    },
    auditLogsPagination() {
      return this.$store.state.appConnector.auditLogsPagination
    },
    webhookLogs() {
      return this.$store.state.appConnector.webhookLogs
    },
    webhookLogsPagination() {
      return this.$store.state.appConnector.webhookLogsPagination
    },
  },
  async mounted() {
    await this.fetchAuditLogs(1)
  },
  methods: {
    async fetchAuditLogs(page) {
      this.auditPage = page || this.auditPage
      this.loadingAudit = true
      try {
        const filters = {}
        if (this.auditFilters.direction) filters.direction = this.auditFilters.direction
        if (this.auditFilters.status) filters.status = this.auditFilters.status
        if (this.auditFilters.from) filters.from = this.auditFilters.from
        if (this.auditFilters.to) filters.to = this.auditFilters.to

        await this.$store.dispatch('appConnector/fetchAuditLogs', {
          connectorId: this.connectorId,
          page: this.auditPage,
          filters,
        })
      } finally {
        this.loadingAudit = false
      }
    },
    async fetchWebhookLogs(page) {
      this.webhookLogPage = page || this.webhookLogPage
      this.loadingWebhookLogs = true
      try {
        const filters = {}
        if (this.webhookLogFilters.status) filters.status = this.webhookLogFilters.status

        await this.$store.dispatch('appConnector/fetchWebhookLogs', {
          connectorId: this.connectorId,
          page: this.webhookLogPage,
          filters,
        })
      } finally {
        this.loadingWebhookLogs = false
      }
    },
    formatDateTime(val) {
      if (!val) return ''
      return new Date(val).toLocaleString()
    },
  },
}
</script>
